// Super-admin view over pre-tenant onboarding intake: mint a link per prospect,
// watch how far they've got, and read what came back.
//
// Separate from super-admin.tsx because everything there is scoped to an existing
// organization and these submissions exist before one does.

import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { Copy, Check, Loader2, FileDown, ChevronRight, ChevronLeft } from "lucide-react";
import { ANSWER_LABELS, flagsFor, renderAnswer } from "@/lib/onboardingFlags";

type Row = {
  id: number;
  prospectName: string;
  prospectEmail: string | null;
  status: string;
  currentStep: number;
  submittedAt: string | null;
  updatedAt: string | null;
  lastSeenAt: string | null;
  createdAt: string;
};

type Detail = Row & {
  token: string;
  answers: Record<string, any>;
  files: Array<{ id: number; kind: string; filename: string; sizeBytes: number }>;
};

/**
 * Submitting doesn't lock the form, so a prospect can change an answer after you've
 * already built their account from it. A few seconds of slack absorbs the writes that
 * land alongside the submit itself.
 */
function editedAfterSubmit(r: { status: string; submittedAt: string | null; updatedAt: string | null }): boolean {
  if (r.status !== "submitted" || !r.submittedAt || !r.updatedAt) return false;
  return new Date(r.updatedAt).getTime() - new Date(r.submittedAt).getTime() > 5000;
}

export default function SuperAdminOnboarding() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [openId, setOpenId] = useState<number | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [, setLocation] = useLocation();

  const list = useQuery<Row[]>({
    queryKey: ["/api/super-admin/onboarding"],
    queryFn: async () => {
      const res = await fetch("/api/super-admin/onboarding");
      if (!res.ok) throw new Error("unauthorized");
      return res.json();
    },
  });

  const detail = useQuery<Detail>({
    queryKey: [`/api/super-admin/onboarding/${openId}`],
    enabled: openId != null,
    queryFn: async () => {
      const res = await fetch(`/api/super-admin/onboarding/${openId}`);
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
  });

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/super-admin/onboarding", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prospectName: name, prospectEmail: email || null }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message ?? "failed");
      return res.json() as Promise<{ id: number; token: string; prospectName: string }>;
    },
    onSuccess: (d) => {
      setName("");
      setEmail("");
      qc.invalidateQueries({ queryKey: ["/api/super-admin/onboarding"] });
      const url = `${window.location.origin}/onboard/${d.token}`;
      navigator.clipboard?.writeText(url).catch(() => {});
      toast({ title: "Link created and copied", description: url });
    },
    onError: (e: any) => toast({ title: "Couldn't create link", description: e.message, variant: "destructive" }),
  });

  const copyLink = (token: string) => {
    const url = `${window.location.origin}/onboard/${token}`;
    navigator.clipboard?.writeText(url);
    setCopied(token);
    setTimeout(() => setCopied(null), 1500);
  };

  if (list.isError) {
    return (
      <div className="mx-auto max-w-md px-6 py-24 text-center">
        <p className="text-gray-600">Sign in as a super admin to view onboarding submissions.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-10">
      <button
        onClick={() => setLocation("/super-admin")}
        className="mb-4 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
      >
        <ChevronLeft className="h-4 w-4" />
        Back to super admin
      </button>
      <h1 className="text-2xl font-semibold text-gray-900">Tenant onboarding</h1>
      <p className="mt-1 text-gray-600">Create an intake link per prospect and read what comes back.</p>

      <Card className="mt-6 p-5">
        <div className="grid gap-3 sm:grid-cols-[1fr_1fr_auto] sm:items-end">
          <div>
            <Label className="text-sm">Prospect name</Label>
            <Input className="mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="Pitt Produce" />
          </div>
          <div>
            <Label className="text-sm">Email (optional)</Label>
            <Input className="mt-1" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="brad@example.com" />
          </div>
          <Button onClick={() => create.mutate()} disabled={!name.trim() || create.isPending}>
            {create.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Create link
          </Button>
        </div>
      </Card>

      <div className="mt-8 space-y-3">
        {list.isLoading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
        {list.data?.length === 0 && <p className="text-gray-500">No intake links yet.</p>}

        {list.data?.map((r) => {
          const open = openId === r.id;
          return (
            <Card key={r.id} className="overflow-hidden">
              <button
                className="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-gray-50"
                onClick={() => setOpenId(open ? null : r.id)}
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-gray-900">{r.prospectName}</span>
                    <Badge variant={r.status === "submitted" ? "default" : "secondary"}>
                      {r.status === "submitted" ? "Submitted" : `In progress · step ${r.currentStep + 1}`}
                    </Badge>
                    {/* The form stays editable after submitting, so an answer can change
                        under an account already built from it. Surface that rather than
                        prevent it. */}
                    {editedAfterSubmit(r) && (
                      <Badge variant="outline" className="border-amber-400 text-amber-700">
                        Edited since
                      </Badge>
                    )}
                  </div>
                  <p className="mt-0.5 truncate text-sm text-gray-500">
                    {r.prospectEmail ?? "no email"}
                    {r.lastSeenAt && ` · last opened ${new Date(r.lastSeenAt).toLocaleString()}`}
                  </p>
                </div>
                <ChevronRight className={`h-5 w-5 shrink-0 text-gray-400 transition ${open ? "rotate-90" : ""}`} />
              </button>

              {open && (
                <div className="border-t bg-gray-50 p-5">
                  {detail.isLoading && <Loader2 className="h-5 w-5 animate-spin text-gray-400" />}
                  {detail.data && (
                    <>
                      <div className="mb-4 flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => copyLink(detail.data!.token)}>
                          {copied === detail.data.token ? (
                            <Check className="mr-2 h-4 w-4 text-green-600" />
                          ) : (
                            <Copy className="mr-2 h-4 w-4" />
                          )}
                          Copy intake link
                        </Button>
                      </div>

                      {flagsFor(detail.data.answers, detail.data.files).length > 0 && (
                        <div className="mb-5 rounded-lg border border-amber-300 bg-amber-50 p-4">
                          <p className="mb-2 text-sm font-semibold text-amber-900">Before the session</p>
                          <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">
                            {flagsFor(detail.data.answers, detail.data.files).map((f, i) => (
                              <li key={i}>{f}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {detail.data.files.length > 0 && (
                        <div className="mb-5">
                          <p className="mb-2 text-sm font-semibold text-gray-700">Uploads</p>
                          <div className="space-y-2">
                            {detail.data.files.map((f) => (
                              <a
                                key={f.id}
                                href={`/api/super-admin/onboarding/${detail.data!.id}/files/${f.id}`}
                                className="flex items-center gap-2 text-sm text-blue-700 hover:underline"
                              >
                                <FileDown className="h-4 w-4" />
                                {f.filename}
                                <span className="text-gray-400">
                                  ({Math.round(f.sizeBytes / 1024)} KB · {f.kind.replace(/_/g, " ")})
                                </span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      <dl className="divide-y divide-gray-200 rounded-lg bg-white px-4">
                        {Object.entries(detail.data.answers).map(([k, v]) => (
                          <div key={k} className="grid gap-1 py-3 sm:grid-cols-[220px_1fr]">
                            <dt className="text-sm text-gray-500">{ANSWER_LABELS[k] ?? k}</dt>
                            <dd className="whitespace-pre-wrap text-sm text-gray-900">{renderAnswer(k, v)}</dd>
                          </div>
                        ))}
                        {Object.keys(detail.data.answers).length === 0 && (
                          <p className="py-3 text-sm text-gray-500">Nothing filled in yet.</p>
                        )}
                      </dl>
                    </>
                  )}
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
